import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import type { UploadedFile } from '@/components/files';
import { SearchLauncher } from '@/components/search/SearchLauncher';
import { PROMPT_BAR_GLOW_CLASS } from '@/lib/components/promptBarGlow';
import { logger } from '@/lib/logger';
import { ConversationInput } from '@/lib/reasoning/ConversationInput';
import { cx } from '@/utils/cx';

export interface QuickPrompt {
  id: string;
  text: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface DashboardHeaderProps {
  /** Greeting text (e.g., "Good morning") */
  greeting: string;
  /** User's first name */
  firstName: string | null;
  /** Whether the user data is still loading */
  isUserLoading: boolean;
  /** Total number of meetings today */
  meetingsCount: number;
  /** Number of meetings still upcoming (start_time in the future) */
  meetingsUpcoming: number;
  /** Number of actions */
  actionsCount: number;
  /**
   * Whether the meeting/action counts are still loading. While true the
   * subtitle shows the date and a shimmer where the counts will go, instead of
   * the zero-count sentence that would be rewritten a moment later.
   */
  isCountsLoading?: boolean;
  /** Number of actions created today (used for recap-mode subtitle). */
  todayActionCount?: number;
  /** When true, the subtitle uses recap-mode phrasing focused on today's wrap-up. */
  isRecapMode?: boolean;
  /** Whether data is processing */
  isProcessing: boolean;
  /** Whether the input is in submitting state */
  isSubmitting?: boolean;
  /** Callback when user submits input */
  onSubmit: (value: string) => void | Promise<void>;
  /** Current input value (controlled) */
  inputValue: string;
  /** Callback to update the input value */
  onInputChange: (value: string) => void;
  /** Optional placeholder text for input */
  placeholder?: string;
  /** Optional ref to attach to the primary input element (for focus restoration) */
  inputRef?: React.MutableRefObject<HTMLElement | null>;
  /** Additional className */
  className?: string;
  /** IANA timezone for date display (e.g. "America/New_York"). Falls back to browser local time. */
  timeZone?: string;
  /** Org ID for file upload */
  orgId?: number;
  /** Currently uploaded files */
  uploadedFiles?: UploadedFile[];
  /** Callback when files change */
  onFilesChange?: (files: UploadedFile[]) => void;
  /**
   * A read-only (viewer) seat. The hero slot renders the global search
   * launcher instead of the chat composer — a viewer can't ask the agent
   * anything, but finding a record is what they came to do — and the
   * subtitle drops the "Ready to help you today" line, which promises AI
   * work this seat can't ask for, in favour of a line about the team's
   * activity the viewer is here to watch.
   */
  isReadOnlySeat?: boolean;
}

/**
 * DashboardHeader - Compact header for Dashboard V2
 *
 * Features:
 * - Smaller greeting text (18px) with subtitle stats
 * - Meeting count and action count in subtitle
 * - Processing message in subtitle when processing
 * - Central input bar for starting conversations
 */
export function DashboardHeader({
  greeting,
  firstName,
  isUserLoading,
  meetingsCount,
  meetingsUpcoming,
  actionsCount,
  isCountsLoading = false,
  todayActionCount = 0,
  isRecapMode = false,
  isProcessing,
  isSubmitting = false,
  onSubmit,
  placeholder,
  inputRef,
  className,
  timeZone,
  inputValue,
  onInputChange,
  orgId,
  uploadedFiles,
  onFilesChange,
  isReadOnlySeat = false,
}: DashboardHeaderProps) {
  const inputAreaRef = useRef<HTMLDivElement>(null);

  // Attach inputRef to the contentEditable element inside the input area (for focus restoration)
  useEffect(() => {
    if (inputRef && inputAreaRef.current) {
      const el = inputAreaRef.current.querySelector<HTMLElement>(
        '[contenteditable="true"]'
      );
      if (el) {
        inputRef.current = el;
      }
    }
  }, [inputRef]);

  const handleSubmit = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      try {
        await onSubmit(trimmed);
        onInputChange('');
      } catch (error) {
        logger.error('Failed to submit prompt', error as Error, {
          component: 'DashboardHeader',
          error_type: (error as Error)?.name ?? 'UnknownError',
        });
        toast.error('Unable to process request. Please try again.');
      }
    }
  }, [inputValue, onSubmit, onInputChange]);

  // Build the greeting text
  const greetingText = firstName ? `${greeting}, ${firstName}` : greeting;

  // Build subtitle with date and meeting/action counts
  const buildSubtitle = (): React.ReactNode => {
    const now = new Date();
    const tzOptions = timeZone ? { timeZone } : {};
    let dayName: string;
    let monthDay: string;
    try {
      dayName = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        ...tzOptions,
      }).format(now);
      monthDay = new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        ...tzOptions,
      }).format(now);
    } catch {
      // Invalid timeZone — fall back to browser local time
      dayName = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
      }).format(now);
      monthDay = new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
      }).format(now);
    }

    // Counts still loading: keep the sentence's first half (it is known) and
    // reserve the rest of the line, so the text is written once, not twice.
    if (isCountsLoading) {
      return (
        <>
          It&apos;s {dayName}, {monthDay}.{' '}
          <span
            aria-hidden='true'
            className='inline-block h-[14px] w-56 max-w-full align-middle rounded bg-tertiary animate-pulse'
          />
        </>
      );
    }

    // Build the meeting/action parts (as JSX for bold styling)
    // Show upcoming count when some meetings have already started,
    // otherwise show total count. When no meetings are upcoming but
    // some may still be in progress, omit the meetings blurb entirely
    // rather than claiming "no more meetings" which could be misleading.
    const hasPastMeetings =
      meetingsCount > 0 && meetingsUpcoming < meetingsCount;
    const displayMeetingCount = hasPastMeetings
      ? meetingsUpcoming
      : meetingsCount;
    let meetingsPart: React.ReactNode = null;
    if (displayMeetingCount > 0) {
      meetingsPart = (
        <strong key='meetings'>
          {displayMeetingCount}
          {hasPastMeetings ? ' more' : ''} meeting
          {displayMeetingCount !== 1 ? 's' : ''} today
        </strong>
      );
    }
    const actionsLabel = isRecapMode ? 'new action' : 'recommended action';
    const actionsCountToShow = isRecapMode ? todayActionCount : actionsCount;
    const actionsPart =
      actionsCountToShow > 0 ? (
        <strong key='actions'>
          {actionsCountToShow} {actionsLabel}
          {actionsCountToShow !== 1 ? 's' : ''}
          {isRecapMode ? ' today' : ''}
        </strong>
      ) : null;

    // Build the counts part with "and" connector
    const countsParts: React.ReactNode[] = [];
    if (meetingsPart) {
      countsParts.push(meetingsPart);
    }
    if (actionsPart) {
      if (countsParts.length > 0) {
        countsParts.push(' and ');
      }
      countsParts.push(actionsPart);
      if (!isRecapMode) {
        countsParts.push(' to review');
      }
    }

    const datePrefix = `It's ${dayName}, ${monthDay}.`;

    // A viewer has no AI actions to review and nothing is being generated
    // for them, so the count/processing sentences never apply; only their
    // meetings (if any) do.
    if (isReadOnlySeat) {
      return countsParts.length === 0 ? (
        <>{datePrefix} Here&apos;s what your team is working on.</>
      ) : (
        <>
          {datePrefix} You have {countsParts}.
        </>
      );
    }

    if (countsParts.length === 0) {
      return isProcessing ? (
        <>
          {datePrefix} Ready to help you today. We're importing your data and
          generating relevant actions.
        </>
      ) : (
        <>{datePrefix} Ready to help you today.</>
      );
    }

    return isProcessing ? (
      <>
        {datePrefix} You have {countsParts}. We're importing your data and
        generating relevant actions.
      </>
    ) : (
      <>
        {datePrefix} You have {countsParts}.
      </>
    );
  };

  const subtitle = buildSubtitle();

  // Use default placeholder
  const effectivePlaceholder =
    placeholder ?? 'Draft an email, research a prospect, update a deal';

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleSubmit();
    },
    [handleSubmit]
  );

  return (
    <div className={cx('flex flex-col items-start', className)}>
      {/* Compact greeting */}
      <h1
        className='text-[22px] sm:text-[24px] font-medium text-[var(--color-text-primary)] mb-3'
        aria-busy={isUserLoading}
      >
        {isUserLoading ? (
          <>
            {greeting},&nbsp;
            <span className='inline-block w-20 h-5 bg-secondary rounded align-middle' />
          </>
        ) : (
          greetingText
        )}
      </h1>

      {/* Subtitle with meeting/action counts */}
      <p
        className='text-[14px] mb-6 text-[var(--color-text-secondary)]'
        aria-busy={isCountsLoading}
      >
        {subtitle}
      </p>

      {/* Main input - full width */}
      <div className='w-full relative' ref={inputAreaRef}>
        {isReadOnlySeat ? (
          /* Read-only seat: the hero is the way into the global search —
             the one thing in this slot a viewer can actually use. Chat is
             still discoverable (and paywalled) from the locked nav item. */
          <SearchLauncher />
        ) : (
          <ConversationInput
            value={inputValue}
            onChange={onInputChange}
            onSubmit={handleFormSubmit}
            placeholder={effectivePlaceholder}
            variant='simple'
            sticky={false}
            loading={isSubmitting}
            autoFocus
            compactUntilWrap
            orgId={orgId}
            uploadedFiles={uploadedFiles}
            onFilesChange={onFilesChange}
            enableVoice
            className={PROMPT_BAR_GLOW_CLASS}
          />
        )}
      </div>
    </div>
  );
}

export default DashboardHeader;
